import _ from 'lodash';
import { mask, string, trimmed, union } from 'superstruct';
import { isElementOf } from '@arc/shared/util-js';
import { emptyStringIsNull, parsableArray, parsableInteger } from '@arc/shared/util-superstruct';
import { firstValue } from '../../nav-action';
import { Lens } from './lens';

/**
 * Use this to specify the type that a lens is getting from and setting to.
 *
 * TypeScript cannot infer the type of some lenses because it does not know
 * what the lens will be used on. For example, `const myLens = atPath('foo')`
 * may cause a type error because TypeScript does not know what `myLens` is
 * meant to be used on. TypeScript has to guess and often guesses wrong.
 * Changing to `const myLens = lensFrom<MyObject>().then(atPath('foo'))` will
 * fix the issue. `lensFrom<MyObject>()` tells TypeScript that we are building
 * a lens to get from, and set to, values of type `MyObject`.
 */
export function lensFrom() {
  return new Lens(x => x, x => x);
}
export function atPath(path) {
  return new Lens(p => _.get(p, path), (value, prev) => _.set(_.clone(prev), path, value));
}

/**
 * Sort arrays and remove duplicate elements.
 *
 * This is a function instead of a const because functions can have generic types.
 */
export function sortUniqLens() {
  return new Lens(array => _.sortedUniq(array.toSorted()), array => _.sortedUniq(array.toSorted()));
}
export function inArrayLens(element) {
  return new Lens(array => array.includes(element), (isIn, array) => isIn === array.includes(element) ? array : isIn ? [...array, element] : array.filter(e => e !== element));
}
export function enumLens(elements, defaultElement) {
  return new Lens(value => isElementOf(elements, value) ? value : defaultElement, value => value);
}

/**
 * Perform some additional modification to the source
 * when the lens is used to change its value.
 *
 * Note: A well behaved lens in functional programming would never have
 *       side effects like this. This function was used because it was
 *       convenient at the time. If lenses have gotten complicated try
 *       moving side effects out of the lenses.
 */
export function lensWithOnChange(lens, onChangeSideEffect, isEqual = (a, b) => a === b) {
  return new Lens(lens.get, (b, s) => {
    const a = lens.get(s);
    if (!isEqual(a, b)) {
      s = onChangeSideEffect(s);
    }
    return lens.set(b, s);
  });
}
export function tryMask(value, struct) {
  try {
    return mask(value, struct);
  } catch {
    return undefined;
  }
}

/**
 * Parse and stringify an array of integers.
 *
 * When parsing fails, this returns `[]`.
 *
 * This was originally made to work with URL query parameters.
 * That is why it uses `string | undefined` instead of just `string`.
 */
export function parseIntArrayLens() {
  return new Lens(value => tryMask(value, parsableArray(parsableInteger())) ?? [], value => value.length > 0 ? value.join(',') : undefined);
}
export function parseIntOrStrArrayLens() {
  return new Lens(value => tryMask(value, parsableArray(union([parsableInteger(), trimmed(emptyStringIsNull(string()))]))) ?? [], value => value.length > 0 ? value.join(',') : undefined);
}

/**
 * Parse and stringify an integer.
 *
 * When parsing fails, this returns `undefined`.
 *
 * This was originally made to work with URL query parameters.
 * That is why it uses `string | undefined` instead of just `string`.
 */
export function parseIntLens() {
  return new Lens(value => tryMask(firstValue(value), parsableInteger()), value => typeof value === 'number' ? '' + value : undefined);
}

/**
 * Parse and stringify an integer or string.
 *
 * When parsing fails, this returns `undefined`.
 *
 * This was originally made to work with URL query parameters.
 * That is why it uses `string | undefined` instead of just `string`.
 */
export function parseIntOrStringLens() {
  return new Lens(value => tryMask(firstValue(value), union([parsableInteger(), trimmed(emptyStringIsNull(string()))])), value => typeof value === 'number' ? '' + value : typeof value === 'string' ? value : undefined);
}

/**
 * Parse and stringify an boolean.
 *
 * When parsing fails, this returns `undefined`.
 *
 * This was originally made to work with URL query parameters.
 * That is why it uses `string | undefined` instead of just `string`.
 */
export function parseBooleanLens() {
  return new Lens(value => {
    if (value === 'false') return false;
    if (value === 'true') return true;
    return undefined;
  }, value => typeof value === 'boolean' ? '' + value : undefined);
}